import { ActionType } from '../../../core/redux/actionType';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../../core/api/module';
import * as R from 'ramda';
import { Endpoints } from '../../../core/api/endpoints';
import { Rater } from './models';
import { AppState } from '../../../core/redux/appState';


const KEY_ACTION = ActionType.UpdateInviteDataRequest;
const ACTIONS    = [
  ActionType.UpdateInviteDataRequest,
  ActionType.UpdateInviteDataResponse,
  ActionType.UpdateInviteDataError,
];


type Payload = Rater;


type Meta = {
  projectId: number;
  raterId: number;
};


/**
 * Dispatch this to add custom content to the message sent with a rater's invitation
 *
 * @param projectId - the database Id for the project to which this person should be added
 * @param participantId - the database Id for the participant to be rated
 * @param raterId - the rater's permissions level
 * @param comment - the rater's email address
 */
export const updateInviteDataAction = (
  projectId: number,
  participantId: number,
  raterId: number,
  comment: string,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.UpdateInviteData,
    headers:  { 'content-type': 'application/json' },
    body:     state => (JSON.stringify(
        {
          projectId:     projectId,
          participantId: state.auth.currentUser?.personId,
          raterId:       raterId,
          comment:       comment,
        },
      )
    ),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        projectId: projectId,
        raterId:   raterId,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}-${raterId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const metaTag = `${action.meta.projectId}-${action.meta.raterId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  return R.mergeRight(
    successState,
    {
      projectRaters: { [`${action.meta.projectId}`]: { [`${action.meta.raterId}`]: payload! } },
    },
  );
};

export const updateInviteDataReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
